import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';

const ContactSheetPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Get the imageUrl from the location state
    if (location.state?.imageUrl) {
      setImageUrl(location.state.imageUrl);
      setIsLoading(false);
    } else {
      setError('No image data provided');
      setIsLoading(false);
    }
  }, [location.state]);

  const handleDownload = () => {
    if (imageUrl) {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `property-sheet-${new Date().toISOString().slice(0, 10)}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleBack = () => {
    // Navigate back while preserving the showProcessing state
    navigate('/', {
      state: {
        returnFromContactSheet: true,
        ...location.state
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Processing</span>
          </button>
          
          {imageUrl && (
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              <span>Download</span>
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-96 text-red-500">{error}</div>
          ) : (
            <div className="p-4 md:p-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-6">Property Contact Sheet</h1>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {imageUrl && (
                  <img 
                    src={imageUrl} 
                    alt="Property Contact Sheet" 
                    className="w-full h-auto max-w-full"
                    style={{
                      imageRendering: 'high-quality'
                    }}
                  />
                )}
              </div>
              <p className="text-sm text-gray-500 mt-4">
                This contact sheet contains all available images for the property.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactSheetPage;